import { queryClient } from '@/lib/query-client';
import { queryKeys } from '@/lib/query-keys';
import { useAuthStore } from '@/stores/auth-store';
import type { MeResult } from '@/types/auth';
import type { TenantResource } from '@/types/settings';

/**
 * Refleja en el shell (y en la caché) los cambios del negocio activo.
 *
 * `PATCH /api/tenants/:tenantId` cambia datos que el resto del panel ya tiene cargados: el
 * nombre y el logo del negocio viven en el store (`useAuthStore.tenants`, espejo de
 * `GET /api/auth/me`) y son los que pintan el sidebar y el selector de negocio. Sin esto, el
 * usuario guardaría "Tienda Nueva" y el sidebar seguiría diciendo el nombre viejo hasta
 * recargar la página.
 *
 * Mismo patrón que `features/auth/auth-session.ts` (`applyAuthResult`): una función fuera de
 * React, exportada para poder verificarla desde el smoke sin navegador.
 */
export function applyTenantUpdate(tenant: TenantResource): void {
  const state = useAuthStore.getState();

  const tenants = state.tenants.map((item) =>
    item.id === tenant.id
      ? { ...item, nombreEmpresa: tenant.nombreEmpresa, logoUrl: tenant.logoUrl }
      : item,
  );

  state.setTenants(tenants);

  // La caché de `/api/auth/me` es la puerta de entrada de `RequireAuth`: se mantiene coherente.
  const me = queryClient.getQueryData<MeResult>(queryKeys.auth.me());
  if (me) queryClient.setQueryData<MeResult>(queryKeys.auth.me(), { ...me, tenants });

  queryClient.setQueryData(queryKeys.tenants.detail(tenant.id), tenant);
}
